import React, { useEffect, useRef, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Drawer,
	IconButton,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { ParkingLocation } from '../../types/parkingLocation';
import { Ticket } from '../../types/ticket';
import { useActiveVehicle } from '../../context/ActiveVehicleProvider';
import TicketsTableFilterBar, {
	TicketFilter,
} from '../ticketsTable/TicketsTableFilterBar';
import { useLotHistoryInfinite } from '../../hooks/useLotHistoryInfinite';
import LotHistoryTicketCard from './LotHistoryTicketCard';

interface Props {
	open: boolean;
	onClose: () => void;
	parkingLocation: ParkingLocation;
}

const getInitialFilter = (): TicketFilter => {
	const now = dayjs();
	return {
		ticketNumber: '',
		licensePlate: '',
		showLog: true,
		showLogAndWarn: true,
		showTow: true,
		startDate: now.startOf('day').toISOString(),
		endDate: now.endOf('day').toISOString(),
		items_per_page: 1000,
		page: 1,
	};
};

const LotHistoryDrawer: React.FC<Props> = ({
	open,
	onClose,
	parkingLocation,
}) => {
	const [filter, setFilter] = useState<TicketFilter>(getInitialFilter);
	const { setCurrentSearch } = useActiveVehicle();

	useEffect(() => {
		if (open) setFilter(getInitialFilter());
	}, [open]);

	const {
		tickets,
		isLoadingInitial,
		isLoadingMore,
		hasMore,
		loadMore,
	} = useLotHistoryInfinite(open ? parkingLocation?.id : null, filter);

	const scrollRootRef = useRef<HTMLDivElement | null>(null);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!open) return;
		const node = sentinelRef.current;
		if (!node) return;
		if (!hasMore) return;
		const obs = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) loadMore();
			},
			{
				root: scrollRootRef.current,
				rootMargin: '300px 0px',
			},
		);
		obs.observe(node);
		return () => obs.disconnect();
	}, [open, hasMore, loadMore]);

	const handleClose = () => {
		setFilter(getInitialFilter());
		onClose();
	};

	const handleViewLicenseHistory = (ticket: Ticket) => {
		setCurrentSearch({
			licenseNumber: ticket.licenseNumber,
			licenseState: ticket.licenseState,
			parkingLocation,
		});
		handleClose();
	};

	const handleClearFilters = () => {
		setFilter({
			...getInitialFilter(),
			startDate: undefined,
			endDate: undefined,
		});
	};

	const hasFiltersApplied =
		filter.ticketNumber !== '' ||
		filter.licensePlate !== '' ||
		!filter.showLog ||
		!filter.showLogAndWarn ||
		!filter.showTow ||
		!!filter.startDate ||
		!!filter.endDate;

	const isOnlyTodayFilter =
		filter.ticketNumber === '' &&
		filter.licensePlate === '' &&
		filter.showLog &&
		filter.showLogAndWarn &&
		filter.showTow &&
		!!filter.startDate &&
		!!filter.endDate &&
		dayjs(filter.startDate).isSame(dayjs().startOf('day')) &&
		dayjs(filter.endDate).isSame(dayjs().endOf('day'));

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={handleClose}
			PaperProps={{
				sx: {
					width: { xs: '100%', sm: 520, md: 640, lg: 720 },
					display: 'flex',
					flexDirection: 'column',
				},
			}}
		>
			{/* Sticky header */}
			<Box sx={{ p: 1.5, pb: 1 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'space-between',
						gap: 1,
					}}
				>
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="overline"
							color="text.secondary"
							sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
						>
							Lot History
						</Typography>
						<Typography
							variant="h6"
							fontWeight={400}
							sx={{
								lineHeight: 1.2,
								mt: 0.25,
								wordBreak: 'break-word',
							}}
						>
							{parkingLocation?.name}
						</Typography>
					</Box>
					<IconButton
						onClick={handleClose}
						size="small"
						aria-label="Close"
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</Box>
			<Divider />

			{/* Filter bar (reuses shared component) */}
			<Box sx={{ flexShrink: 0 }}>
				<TicketsTableFilterBar
					filter={filter}
					setFilter={setFilter}
					parkingLocation={parkingLocation}
					compact
				/>
			</Box>
			<Divider />

			{/* Scrollable card list */}
			<Box
				ref={scrollRootRef}
				sx={{
					flexGrow: 1,
					overflowY: 'auto',
					p: 1.5,
					bgcolor: 'background.default',
				}}
			>
				{isLoadingInitial && tickets.length === 0 ? (
					<Stack spacing={1.5}>
						{[0, 1, 2].map((i) => (
							<Box key={i}>
								<Skeleton
									variant="rectangular"
									sx={{
										width: '100%',
										aspectRatio: '16 / 9',
										borderRadius: 2,
									}}
								/>
								<Skeleton width="40%" sx={{ mt: 1 }} />
								<Skeleton width="80%" />
							</Box>
						))}
					</Stack>
				) : tickets.length === 0 ? (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 1.5,
							py: 6,
							textAlign: 'center',
						}}
					>
						<Typography variant="body2" color="text.secondary">
							{isOnlyTodayFilter
								? 'No records logged today.'
								: hasFiltersApplied
								? 'No records match these filters.'
								: 'No records logged at this lot yet.'}
						</Typography>
						{hasFiltersApplied && (
							<Button
								size="small"
								variant="outlined"
								onClick={handleClearFilters}
							>
								{isOnlyTodayFilter ? 'See all records' : 'Clear filters'}
							</Button>
						)}
					</Box>
				) : (
					<Stack spacing={1.5}>
						{tickets.map((ticket) => (
							<LotHistoryTicketCard
								key={ticket.ticketNumber}
								ticket={ticket}
								parkingLocation={parkingLocation}
								onViewLicenseHistory={handleViewLicenseHistory}
							/>
						))}
					</Stack>
				)}

				<Box
					ref={sentinelRef}
					sx={{ height: 1, width: '100%' }}
					aria-hidden
				/>

				{tickets.length > 0 && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							gap: 1,
							py: 2,
							minHeight: 40,
						}}
					>
						{isLoadingMore && (
							<>
								<CircularProgress size={16} />
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Loading more…
								</Typography>
							</>
						)}
						{!isLoadingMore && !hasMore && (
							<Typography
								variant="caption"
								color="text.secondary"
							>
								End of list ({tickets.length} record
								{tickets.length === 1 ? '' : 's'})
							</Typography>
						)}
					</Box>
				)}
			</Box>
		</Drawer>
	);
};

export default LotHistoryDrawer;
